// @ts-strict-ignore
import produce from 'immer';
import {AttrsSchema, Component, ConfigOptions} from 'panel';
import uniqueBy from 'lodash/uniqBy';
import copy from 'clipboard-copy';

import {emitTrackEvent} from 'common/util/dom';
import {getEntityCreatorDisplayName, userDetailsStore} from 'common/report/stores/user-details';
import {currentProjectStore} from 'common/report/stores/project-details';
import {removeByIndex} from 'common/util';
import {apiClient} from 'common/util/api-client';
import {TagItem} from 'common/components/tag-input/tag-item';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ProjectMember, TeamObj} from 'common/report/queries/app-api/workspaces-client';
import {Section} from 'common/components/items-menu/types';
import {reporter} from 'common/report/error-reporting';
import {sharedEntitiesClient} from 'common/report/queries/app-api/shared-entities-client';
import {publicDashboardLinksClient} from 'common/report/queries/app-api/public-dashboard-links-client';
import {
  GrantStatus,
  SharableEntityGrant,
  SharableEntityType,
  TeamShareGrant,
  UserRole,
  UserShareGrant,
} from 'common/report/util/saved-entities/types';
import {getEntityType, getEntityTypeName, isSharingEnabledForProject} from 'common/report/util/saved-entities';
import {getWorkspaceId, isStaffEmail} from 'common/report/util';
import {trackUpsellEvent} from 'common/report/util/tracking-helpers';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {
  getPublicDashboardLink,
  getPublicDashboardLinkOverflowButton,
  OverflowActions,
} from 'common/templates/public-dashboards/mixins';
import {FeatureGate} from 'common/report/constants';
import {ProjectMemberResponse} from 'common/types/app-api/project-member';
import {TagInputChangeAction} from 'common/components/tag-input/types';
import {getLocation} from 'common/util/url';
import {IronContextRegistry} from 'common/report/context-registry';
import {Translatable} from 'common/i18n';
import {getUpsellInfo, ModalTrigger} from 'common/widgets/in-product-upsell-modal/util';

import {LoadingStatus} from '../types';
import {
  commonSharingTrackProps,
  CopyLinkUISource,
  eventPrefix,
  ShareModalType,
  SharingTrackArgs,
  track,
} from '../tracking';
import {sharingUtils} from '../utils';
import {
  PublicDashboardLinkRow,
  ShareOptionsLocation,
  SharingModalAttrs,
  SharingModalState,
  SharingModalViewType,
} from './types';
import {template} from './template';
import {getPublicDashboardLinkEnabled, getWorkspaceName} from './helpers';
import css from './index.inline.styl';

const enum ProjectShareLevel {
  Off = `off`,
  Read = `read`,
  Edit = `edit`,
}

type TeamOrProjectMember = TeamObj | ProjectMember;

const selectOptions = [
  {
    items: [
      {label: ({t}) => t(`mixology.user-access.viewer`), value: `read`},
      {label: ({t}) => t(`mixology.user-access.editor`), value: `edit`},
    ],
  },
];

const selectOptionsWithRemoveOption = [...selectOptions];
selectOptionsWithRemoveOption.push({
  items: [{label: ({t}) => t(`components.sharing.remove`), value: `remove`}],
});

const statusLabelMap = {
  [GrantStatus.New]: ({t}) => t(`components.sharing.tag-added`),
  [GrantStatus.Updated]: ({t}) => t(`components.sharing.tag-updated`),
};

function isProjectMember(teamOrProjectMember: TeamOrProjectMember): teamOrProjectMember is ProjectMember {
  return Object.prototype.hasOwnProperty.call(teamOrProjectMember, `email`);
}

function getSectionData(
  membersOrTeamsState: any,
  pendingGrants: Set<TeamOrProjectMember>,
  currentGrants: SharableEntityGrant,
): Partial<Section<TeamOrProjectMember>> {
  if (membersOrTeamsState.loading !== LoadingStatus.Success) {
    return {isLoading: true, items: []};
  }

  return {
    isLoading: false,
    items: membersOrTeamsState.data.map((member) => ({
      label: isProjectMember(member) ? `${member.name} <${member.email}>` : member.name,
      value: member,
      isDisabled:
        pendingGrants.has(member) ||
        Boolean(
          isProjectMember(member)
            ? member.email === currentGrants.owner?.email ||
                currentGrants.userShares.find((u) => u.email === member.email)
            : currentGrants.teamShares.find((t) => t.id === member.id),
        ),
    })),
  };
}

export function getFormattedUserName(name: string, email: string): Translatable {
  const displayName = getEntityCreatorDisplayName(email, name);
  return email === userDetailsStore.value.email
    ? ({t}) => displayName + ` (${t(`components.sharing.you`)})`
    : displayName;
}

export class SharingModal extends Component<
  SharingModalState,
  SharingModalAttrs,
  unknown,
  unknown,
  IronContextRegistry
> {
  private declare copyLinkInputEl: HTMLInputElement;
  private currentProjectStoreCallback = () => {
    this.update({projectStoreLoading: LoadingStatus.Success});
    if (this.helpers.isSharingEnabledForProject()) {
      this.getAvailableTeams();
      const shouldTakeSnapshot = this.state.currentGrantsSnapshot.data === null;
      // When the share modal components load, we take a snapshot of current grants
      // to determine whether a users/teams are newly added,updated or no changes are made
      // to the sharing grants
      this.getCurrentShareGrants(shouldTakeSnapshot);
    } else {
      // If projectStore is fetched after modal opens, we fetch shortURL here for free plan (sharing not enabled)
      this.getShortUrl(true);
    }
    this.getAvailableMembers();
    // Fetch all available Public Dashboard Links
    this.getPublicDashboardLinks();
  };

  static get attrsSchema(): AttrsSchema<SharingModalAttrs> {
    return {
      ...super.attrsSchema,
      open: `boolean`,
      type: `string`,
      id: `string`,
      'entity-name': `string`,
      'project-id': `number`,
      disabled: `boolean`,
      'disabled-reason': `string`,
      'success-message': `string`,
      'open-as': `string`,
      'is-superadmin': `boolean`,
      'open-password-row-item': `json`,
      'entity-owner-id': `number`,
    };
  }

  get config(): ConfigOptions<SharingModalState, unknown, IronContextRegistry> {
    return {
      css,
      useShadowDom: true,
      template,
      contexts: [`i18n` as const, `currentProject` as const],
      defaultState: {
        pendingCanEdit: false,
        pendingGrants: new Set<TeamOrProjectMember>(),
        searchText: ``,
        shortUrl: ``,
        shortUrlFetchStatus: `success`,
        shortUrlLoading: false,
        showCopyLinkText: false,
        showPopover: false,
        availableMembers: {
          loading: LoadingStatus.Idle,
          data: null,
          error: null,
        },
        availableTeams: {
          loading: LoadingStatus.Idle,
          data: null,
          error: null,
        },
        currentGrants: {
          loading: LoadingStatus.Idle,
          data: null,
          error: null,
        },
        publicDashboardLinks: {
          loading: LoadingStatus.Idle,
          data: null,
          error: null,
        },
        currentPublicDashboardLink: null,
        currentGrantsSnapshot: {
          data: null,
        },
        projectStoreLoading: LoadingStatus.Idle,
        publicDashboardEnabled: false,
        modalViewType: SharingModalViewType.Sharing,
        expandedPublicDashboardRows: new Set(),
        upsellInfo: null,
      },
      helpers: {
        getEntityName: () => this.attr(`entity-name`),
        isProjectStoreLoaded: () => this.state.projectStoreLoading === LoadingStatus.Success,
        isSharingEnabledForProject: () => isSharingEnabledForProject(currentProjectStore.value),
        hasWorkspacesFeatureGate: () =>
          this.helpers.isProjectStoreLoaded() && currentProjectStore.value.getFeatureGate(FeatureGate.Workspaces),
        handleCopyLinkInputClick: () => this.copyLinkInputEl.select(),
        shouldShowCopyLinkSpinner: () => {
          // when public dashboard is not enabled, we just wait on short url fetch
          if (!this.helpers.publicDashboard.showPublicDashboardSwitch()) {
            return this.state.shortUrlLoading;
          }
          return this.state.shortUrlLoading || this.state.publicDashboardLinks.loading === LoadingStatus.Pending;
        },
        getCurrentWorkspaceName: () =>
          currentProjectStore.value.workspaces[getWorkspaceId()]?.name ?? ` the Current Data View`,
        handleUpsellButtonClick: () => {
          track.sharingUpsellBannerClick();
          trackUpsellEvent(`Click Upsell`, `sharing`);
        },
        shouldShowUpsellBanner: () => this.helpers.isProjectStoreLoaded() && !this.helpers.isSharingEnabledForProject(),
        handleCopyLinkInputInsert: (inputEl: HTMLInputElement) => {
          this.copyLinkInputEl = inputEl;
          this.copyLinkInputEl.value = this.state.shortUrl;
        },

        isSuccessfullyLoaded: (...args: Array<{loading: LoadingStatus}>) =>
          args.every((arg) => arg.loading === LoadingStatus.Success),

        getEntityTypeTitle: () => this.print(getEntityTypeName(getEntityType(this.attr(`type`)), {titleCased: true})),

        handleDoneButtonClick: (_ev) => {
          this.dispatchEvent(new CustomEvent(`submit`));
        },

        handleHelpClick: () => {
          this.track(track.helpClick());
        },
        getSelectOptions: (shareOptionsLocation: ShareOptionsLocation) => {
          switch (shareOptionsLocation) {
            case ShareOptionsLocation.ProjectShare:
              return [
                {
                  items: [{label: this.t(`components.sharing.off`), value: `off`}, ...selectOptions[0].items],
                },
              ];
            case ShareOptionsLocation.PickerControls:
              return selectOptions;
            default:
              return selectOptionsWithRemoveOption;
          }
        },

        getProjectMemberCount: () => this.state.availableMembers.data?.length,

        getProjectShareLabel: () => {
          return this.t(`components.sharing.give-everyone`);
        },

        getProjectShareGrantStatus: () => {
          const originalProjectShare = this.state.currentGrantsSnapshot.data.projectShares;
          const projectShare = this.state.currentGrants.data.projectShares;
          let projectShareGrantStatus = GrantStatus.Existing;

          if (
            originalProjectShare.length !== projectShare.length ||
            (originalProjectShare.length &&
              projectShare.length &&
              originalProjectShare[0].canEdit !== projectShare[0].canEdit)
          ) {
            projectShareGrantStatus = GrantStatus.Updated;
          }
          return this.helpers.getShareGrantStatusText(projectShareGrantStatus);
        },

        getProjectShareSelectOption: () => {
          if (!this.helpers.isSharingEnabledForProject()) {
            return this.t(`mixology.user-access.editor`);
          }
          const projectShares = this.state.currentGrants.data.projectShares;
          if (projectShares.length) {
            return projectShares[0].canEdit
              ? this.t(`mixology.user-access.editor`)
              : this.t(`mixology.user-access.viewer`);
          }
          return this.t(`components.sharing.off`);
        },

        handleProjectShareSelectChange: ({detail}: any) => {
          const {item} = detail;
          this.updateProjectShareGrantNew(
            item.value === `off`
              ? ProjectShareLevel.Off
              : item.value === `edit`
                ? ProjectShareLevel.Edit
                : ProjectShareLevel.Read,
          );
        },

        handleProjectShareCheckboxChange: () => {
          const isProjectShared = Boolean(this.state.currentGrants.data.projectShares.length);
          if (isProjectShared) {
            this.removeProjectShareGrant();
          } else {
            this.addProjectShareGrant();
          }
        },

        entityHasOwner: () => {
          if (this.helpers.isSharingEnabledForProject()) {
            return Boolean(this.state.currentGrants.data.owner);
          }
          return this.state.availableMembers.data.find((member) => member.id === this.attr(`entity-owner-id`));
        },

        getEntityOwner: () => {
          if (this.helpers.isSharingEnabledForProject()) {
            const {id, email, name} = this.state.currentGrants.data.owner;
            return {id, email, name, formattedName: this.print(getFormattedUserName(name, email))};
          }
          const owner = this.state.availableMembers.data.find((member) => member.id === this.attr(`entity-owner-id`));
          return {
            id: owner.id,
            email: owner.email,
            name: owner.name,
            formattedName: this.print(getFormattedUserName(owner.name, owner.email)),
          };
        },

        getShareGrantDisplayName: (shareGrant: TeamOrProjectMember): string => {
          if (isProjectMember(shareGrant)) {
            return this.print(getFormattedUserName(shareGrant.name, shareGrant.email));
          }
          return shareGrant.name;
        },

        getShareGrantEmail: (email: string): string => {
          if (isStaffEmail(email) && !userDetailsStore.value.isStaff) {
            return null;
          }

          return email;
        },

        getShareGrantStatusText: (grantStatus: GrantStatus): string => this.print(statusLabelMap[grantStatus]) || ``,

        projectHasIntegrated: () => {
          return currentProjectStore.value.hasIntegratedArb || currentProjectStore.value.hasIntegratedEngage;
        },

        showExistingSharesSection: () => {
          const {currentGrants, availableMembers, availableTeams} = this.state;
          if (this.helpers.isSharingEnabledForProject()) {
            return this.helpers.isSuccessfullyLoaded(currentGrants, availableMembers, availableTeams);
          }
          return this.helpers.isSuccessfullyLoaded(availableMembers);
        },

        showProjectShareSection: () => {
          const {currentGrants, availableMembers, availableTeams, publicDashboardLinks} = this.state;
          if (this.helpers.isSharingEnabledForProject()) {
            return this.helpers.isSuccessfullyLoaded(
              currentGrants,
              availableMembers,
              availableTeams,
              publicDashboardLinks,
            );
          }
          return this.helpers.isSuccessfullyLoaded(availableMembers, publicDashboardLinks);
        },
        shortUrl: {
          handleCopyLinkClick: () => {
            const isSharingLexicon = this.state.currentGrants.data.link.startsWith(`lexicon`);
            if (this.state.publicDashboardEnabled && !isSharingLexicon) {
              this.helpers.shortUrl.copyPublicDashboardUrl();
            } else {
              this.helpers.shortUrl.copyInternalShortUrl();
            }
          },
          copyPublicDashboardUrl: async () => {
            try {
              if (this.state.publicDashboardLinks.loading === LoadingStatus.Failed) {
                this.getPublicDashboardLinks();
                return;
              }
              // intelligent routing enabled public link
              const publicUrl = sharingUtils.createPublicDashboardLink(
                this.state.currentPublicDashboardLink.public_link_id,
              );
              await copy(publicUrl);
              addToast({
                message: `URL copied!`,
              });
            } catch {
              addToast({
                message: `Could not copy the URL`,
                type: ToastType.ERROR,
              });
            }
          },
          copyInternalShortUrl: async () => {
            try {
              if (this.state.shortUrlFetchStatus === `failed`) {
                await this.getShortUrl();
                this.copyLinkInputEl.value = this.state.shortUrl;
              }
              this.track(track.shortLink(this.state.shortUrl, CopyLinkUISource.ShareModal));
              await copy(this.state.shortUrl);
              addToast({
                message: `URL copied!`,
              });
              this.update({shortUrlLoading: false});
            } catch {
              this.update({
                shortUrlLoading: false,
                showCopyLinkText: true,
              });
              addToast({
                message: `Could not copy the URL`,
                type: ToastType.ERROR,
              });
              this.track(track.shortLinkCopyFailed(CopyLinkUISource.ShareModal));
            }
          },
        },
        picker: {
          getMenuSections: (): Section[] => {
            const {availableMembers, availableTeams, pendingGrants, currentGrants} = this.state;

            return [
              {
                label: `Teams`,
                ...getSectionData(availableTeams, pendingGrants, currentGrants.data),
              },
              {
                label: `Users`,
                ...getSectionData(availableMembers, pendingGrants, currentGrants.data),
              },
            ];
          },

          getTags: (): TagItem[] => {
            return Array.from(this.state.pendingGrants).map<TagItem>((teamOrMember) => ({
              label: teamOrMember.name,
              value: isProjectMember(teamOrMember) ? teamOrMember.email : teamOrMember.name,
            }));
          },

          handleInputChange: ({detail}) => {
            this.update({showPopover: true});
            if (detail.action === TagInputChangeAction.Text) {
              this.update({searchText: detail.value});
            } else if (detail.action === TagInputChangeAction.RemoveTag) {
              this.update(
                produce((d: SharingModalState) => {
                  d.pendingGrants.forEach((grant) => {
                    if (
                      grant.name === detail.tag.value ||
                      (isProjectMember(grant) && grant.email === detail.tag.value)
                    ) {
                      d.pendingGrants.delete(grant);
                    }
                  });
                }),
              );
              this.updateSessionData();
            }
          },

          handleItemClick: (ev) => {
            const value: TeamOrProjectMember = ev.detail.item.value;
            const foundItem: TeamOrProjectMember = isProjectMember(value)
              ? this.state.availableMembers.data.find((i) => i.id === value.id)
              : this.state.availableTeams.data.find((i) => i.id === value.id);

            this.update(
              produce((d: SharingModalState) => {
                d.pendingGrants.add(foundItem);
                d.searchText = ``;
              }),
            );
            this.updateSessionData();
          },

          handleInputFocus: () => {
            this.update({showPopover: true});
          },

          handlePopoverChange: ({detail}) => {
            if (detail.state === `closed`) {
              this.update({showPopover: false});
            }
          },

          handleShareButtonClick: () => {
            this.savePendingGrants();
          },

          handleLevelSelectChange: ({detail}) => {
            const {item} = detail;
            this.update({pendingCanEdit: item.value === `edit`});
          },
        },
        existingShares: {
          getProjectGrants: () => {
            if (this.helpers.isSuccessfullyLoaded(this.state.currentGrants)) {
              const {projectShares} = this.state.currentGrants.data;
              return !projectShares || projectShares.length < 1 ? null : projectShares;
            }
            return null;
          },

          getUserGrants: () => {
            if (this.helpers.isSuccessfullyLoaded(this.state.currentGrants)) {
              const {userShares} = this.state.currentGrants.data;
              const currentGrantSanpshoptUserIds = this.state.currentGrantsSnapshot.data.userShares.map(
                (user) => user.id,
              );
              const comparatorFunction = (a, b) => {
                // force newly added users to appear on the top
                // need to cover b's comparison as well for firefox's sorting algorithm
                if (!currentGrantSanpshoptUserIds.includes(a.id) && currentGrantSanpshoptUserIds.includes(b.id)) {
                  return -1;
                }
                if (currentGrantSanpshoptUserIds.includes(a.id) && !currentGrantSanpshoptUserIds.includes(b.id)) {
                  return 1;
                }
                return a.name.split(` `)[0].localeCompare(b.name.split(` `)[0]);
              };
              return !userShares || userShares.length < 1 ? null : [...userShares].sort(comparatorFunction); // array is frozen. need to copy the array before sort.
            }
            return undefined;
          },

          getTeamGrants: () => {
            if (this.helpers.isSuccessfullyLoaded(this.state.currentGrants)) {
              const {teamShares} = this.state.currentGrants.data;
              return !teamShares || teamShares.length < 1 ? null : teamShares;
            }
            return undefined;
          },

          getGrantStatus: (
            originalShare: UserShareGrant | TeamShareGrant,
            newShare: UserShareGrant | TeamShareGrant,
          ): GrantStatus => {
            return originalShare
              ? newShare.canEdit !== originalShare.canEdit
                ? GrantStatus.Updated
                : GrantStatus.Existing
              : GrantStatus.New;
          },

          getShareItem: (item: {email?: string; id?: number}): TeamOrProjectMember => {
            if (item.email) {
              const originalUser = this.state.currentGrantsSnapshot.data.userShares.find((m) => m.email === item.email);
              const user = this.state.currentGrants.data.userShares.find((m) => m.email === item.email);
              return {
                ...this.state.availableMembers.data.find((m) => m.email === item.email),
                grantStatus: this.helpers.existingShares.getGrantStatus(originalUser, user),
              };
            } else if (item.id) {
              const originalTeam = this.state.currentGrantsSnapshot.data.teamShares.find((t) => t.id === item.id);
              const team = this.state.currentGrants.data.teamShares.find((t) => t.id === item.id);
              return {
                ...this.state.availableTeams.data.find((t) => t.id === item.id),
                grantStatus: this.helpers.existingShares.getGrantStatus(originalTeam, team),
              };
            }
            return null;
          },

          getTeamMembersCount: (team: TeamObj): number => team?.members?.length,

          handleShareLevelSelectChange: ({detail}: any, shareGrant: TeamOrProjectMember) => {
            const {item} = detail;
            if (item.value === `remove`) {
              this.removeExistingGrant(shareGrant);
            } else {
              this.updateExistingGrant(shareGrant, item.value === `edit`);
            }
          },

          handleDeleteButtonClick: (shareGrant: TeamOrProjectMember) => this.removeExistingGrant(shareGrant),
        },
        publicDashboard: {
          handlePublicDashboardDoneClick: () => this.dispatchEvent(new CustomEvent(`close`, {})),
          getPublicDashboardLinksTableRows: (): PublicDashboardLinkRow[] => {
            const openPasswordRowItem = this.attr(`open-password-row-item`);
            const hasLastSavedPassword = Boolean(openPasswordRowItem?.LastSavedPassword?.length);
            if (this.state.publicDashboardLinks.loading === LoadingStatus.Success) {
              return this.state.publicDashboardLinks.data.map((p) => {
                const createdDate = new Date(p[`created`]).toLocaleDateString(`en-US`, {
                  day: `2-digit`,
                  month: `long`,
                  year: `numeric`,
                });

                return {
                  passwordProtected: p.password_protected,
                  publicLink: p.public_link_id,
                  workspaceId: p.workspace_id,
                  dataView: p.workspace_name,
                  canEdit: p.dashboard.can_update_basic || p.dashboard.is_superadmin,
                  creator: p.creator_name,
                  createdDate,
                  isExpanded: openPasswordRowItem
                    ? p.public_link_id === openPasswordRowItem.PublicLinkId
                    : this.state.expandedPublicDashboardRows.has(p.workspace_id),
                  embedLink:
                    openPasswordRowItem && hasLastSavedPassword
                      ? sharingUtils.createPublicDashboardLink(p.public_link_id) +
                        `?embed=true&passcode=${encodeURIComponent(openPasswordRowItem.LastSavedPassword)}`
                      : ``,
                };
              });
            }
            return [];
          },
          getPublicDashboardLinksTableCols: () => {
            const columns = [
              {
                field: `publicLink`,
                flexBasis: `250px`,
                title: this.t(`components.sharing.boards.public-url`),
                cellRenderer: getPublicDashboardLink,
                type: `link`,
              },
              {
                field: `enabled`,
                flexBasis: `120px`,
                title: this.t(`components.sharing.boards.enabled`),
                cellRenderer: (row) => getPublicDashboardLinkEnabled(row),
              },
              {
                field: `dataView`,
                flexGrow: `1`,
                title: this.t(`components.sharing.boards.data-view`),
                helpText: this.t(`components.sharing.boards.data-view-tooltip`),
                helpLink: `https://help.mixpanel.com/hc/en-us/articles/360043782572-Data-Views`,
                cellRenderer: (row) => getWorkspaceName(row),
              },
              {
                field: `overflow`,
                flexBasis: `80px`,
                cellRenderer: (row) => getPublicDashboardLinkOverflowButton(row, this.handleOverflowSelect.bind(this)),
              },
            ];
            return columns;
          },
          updateSharingModalViewType: (viewType: SharingModalViewType) =>
            this.update({
              modalViewType: viewType,
            }),
          showPublicDashboardSwitch: () => {
            return this.attr(`type`) === SharableEntityType.Dashboard;
          },
          handlePublicDashboardSwitchChange: () => {
            const workspaceId = getWorkspaceId().toString();
            if (this.state.currentPublicDashboardLink) {
              // soft delete public dashboard link
              this.deletePublicDashboardLink(workspaceId);
            } else {
              // no public link was ever generated for the current dashboard.
              this.createPublicDashboardLink(workspaceId);
            }
          },
          updatePublicDashboardEnabled: () => {
            let publicDashboardEnabled = false;
            if (this.state.currentPublicDashboardLink) {
              publicDashboardEnabled = !this.state.currentPublicDashboardLink.deleted;
            }
            this.update({
              publicDashboardEnabled,
            });
          },
          handlePasswordChange: (ev) => {
            this.update(
              produce((draft: SharingModalState) => {
                draft.publicDashboardLinks.data = draft.publicDashboardLinks.data.map((link) => ({
                  ...link,
                  ...(link.public_link_id === ev.detail.publicLinkId
                    ? {password_protected: ev.detail.passwordProtected}
                    : {}),
                }));
              }),
            );
          },
          canToggleFreePublicDashboards: () => {
            return (
              currentProjectStore.value?.role?.name !== UserRole.Consumer &&
              this.helpers.projectHasIntegrated() &&
              currentProjectStore.value?.hasPublicDashboardsEnabled
            );
          },
          projectHasFeatureEnabled: () => {
            return currentProjectStore.value?.hasPublicDashboardsEnabled;
          },
        },
        t: (...args: [any, any]) => this.getContext(`i18n`).t(...args),
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get print() {
    return this.getContext(`i18n`).print;
  }

  async getShortUrl(isFreePlan = false) {
    this.update({shortUrlLoading: true});
    try {
      let shortUrl;
      if (isFreePlan) {
        shortUrl = await sharingUtils.createShortUrl(getLocation().toString());
      } else {
        const entityUrl = sharingUtils.getEntityUrl({
          entityLink: this.state.currentGrants.data.link,
          projectId: this.attr(`project-id`),
          workspaceId: getWorkspaceId(),
        });
        // All reports except JQL should use a point in time url to capture any unsaved state currently in the url.
        if (this.attr(`type`) !== SharableEntityType.Report || entityUrl.includes(`jql-console/#`)) {
          // use entity URL
          shortUrl = await sharingUtils.createShortUrl(entityUrl);
        } else {
          // use point in time url
          shortUrl = await sharingUtils.createShortUrl(getLocation().toString());
        }
      }

      this.update({
        shortUrl,
        shortUrlLoading: false,
        shortUrlFetchStatus: `success`,
      });
    } catch (err) {
      this.update({shortUrlFetchStatus: `failed`, shortUrlLoading: false});
      reporter.error(err, `Could not fetch the short url from the app server`, {
        type: this.attr(`type`),
        id: this.attr(`id`),
      });
    }
  }

  async savePendingGrants(): Promise<void> {
    const projectId = this.attr(`project-id`);
    const sharableEntityType = this.attr(`type`);
    const entityId = this.attr(`id`);
    const {pendingCanEdit, pendingGrants, currentGrants} = this.state;
    const dataBeforeUpdate = currentGrants.data;
    const userShareChange = [];
    const teamShareChange = [];

    this.update(
      produce((draft: SharingModalState) => {
        const shares = draft.currentGrants.data;
        pendingGrants.forEach((g) => {
          if (isProjectMember(g)) {
            userShareChange.push({email: g.email, canEdit: pendingCanEdit});
            shares.userShares.push({id: g.id, name: g.name, email: g.email, canEdit: pendingCanEdit});
          } else {
            teamShareChange.push({
              id: g.id,
              name: this.helpers.existingShares.getShareItem(g).name,
              canEdit: pendingCanEdit,
            });
            shares.teamShares.push({
              id: g.id,
              name: this.helpers.existingShares.getShareItem(g).name,
              canEdit: pendingCanEdit,
            });
          }
        });
        draft.pendingGrants.clear();
        this.clearSessionData();
      }),
    );

    try {
      await sharedEntitiesClient.updateGrants(
        {projectId, entityId, sharableEntityType},
        {
          ...(userShareChange.length > 0 ? {userShares: userShareChange} : {}),
          ...(teamShareChange.length > 0 ? {teamShares: teamShareChange} : {}),
        },
      );
      this.track(
        track.newGrant(pendingCanEdit, {
          usersCount: userShareChange.length,
          teamsCount: teamShareChange.length,
        }),
      );
    } catch (err) {
      reporter.error(err, `Failed to update sharing grants`);
      this.update(
        produce((draft: SharingModalState) => {
          draft.currentGrants.data = dataBeforeUpdate;
        }),
      );
    }
  }

  async updateExistingGrant(shareGrant: TeamOrProjectMember, canEdit: boolean) {
    const projectId = this.attr(`project-id`);
    const sharableEntityType = this.attr(`type`);
    const entityId = this.attr(`id`);

    const userShareChange = isProjectMember(shareGrant);
    const teamShareChange = !userShareChange;

    this.update(
      produce((draft: SharingModalState) => {
        const shares = draft.currentGrants.data;
        if (isProjectMember(shareGrant)) {
          shares.userShares.find((u) => u.email === shareGrant.email).canEdit = canEdit;
        } else {
          shares.teamShares.find((t) => t.id === shareGrant.id).canEdit = canEdit;
        }
      }),
    );

    try {
      const updatedEntity = isProjectMember(shareGrant)
        ? this.state.currentGrants.data.userShares.find((u) => u.email === shareGrant.email)
        : this.state.currentGrants.data.teamShares.find((t) => t.id === shareGrant.id);
      await sharedEntitiesClient.updateGrants(
        {projectId, entityId, sharableEntityType},
        {
          ...(userShareChange ? {userShares: [updatedEntity as UserShareGrant]} : {}),
          ...(teamShareChange ? {teamShares: [updatedEntity as TeamShareGrant]} : {}),
        },
      );
      this.track(
        track.updateGrant(canEdit, userShareChange ? {usersCount: 1, teamsCount: 0} : {usersCount: 0, teamsCount: 1}),
      );
    } catch (err) {
      reporter.error(err, `Failed to update sharing grant`);
      this.getCurrentShareGrants();
    }
  }

  async removeExistingGrant(shareGrant: TeamOrProjectMember) {
    const projectId = this.attr(`project-id`);
    const sharableEntityType = this.attr(`type`);
    const entityId = this.attr(`id`);

    const userShareChange = isProjectMember(shareGrant);
    const teamShareChange = !userShareChange;
    let canEdit;

    this.update(
      produce((draft: SharingModalState) => {
        const shares = draft.currentGrants.data;
        if (isProjectMember(shareGrant)) {
          const idx = shares.userShares.findIndex((u) => u.email === shareGrant.email);
          canEdit = shares.userShares[idx].canEdit;
          shares.userShares = removeByIndex(shares.userShares, idx);
        } else {
          const idx = shares.teamShares.findIndex((t) => t.id === shareGrant.id);
          canEdit = shares.teamShares[idx].canEdit;
          shares.teamShares = removeByIndex(shares.teamShares, idx);
        }
      }),
    );

    try {
      await sharedEntitiesClient.removeGrants(
        {projectId, entityId, sharableEntityType},
        {
          ...(isProjectMember(shareGrant) ? {userShares: [shareGrant.email]} : {}),
          ...(teamShareChange ? {teamShares: [shareGrant.id]} : {}),
        },
      );
      this.track(
        track.removeGrant(canEdit, userShareChange ? {usersCount: 1, teamsCount: 0} : {usersCount: 0, teamsCount: 1}),
      );
    } catch (err) {
      reporter.error(err, `Failed to remove sharing grant`);
      this.getCurrentShareGrants();
    }
  }

  async addProjectShareGrant() {
    const projectId = this.attr(`project-id`);
    const sharableEntityType = this.attr(`type`);
    const entityId = this.attr(`id`);
    const {currentGrants} = this.state;
    const dataBeforeUpdate = currentGrants.data;

    this.update(
      produce((draft: SharingModalState) => {
        const shares = draft.currentGrants.data;
        shares.projectShares.push({
          id: projectId,
          canEdit: false, // project share is view only by default
        });
      }),
    );

    try {
      await sharedEntitiesClient.updateGrants(
        {projectId, entityId, sharableEntityType},
        {projectShares: this.state.currentGrants.data.projectShares},
      );
      this.track(track.newProjectGrant());
    } catch (err) {
      reporter.error(err, `Failed to update sharing grants`);
      this.update(
        produce((draft: SharingModalState) => {
          draft.currentGrants.data = dataBeforeUpdate;
        }),
      );
    }
  }

  async removeProjectShareGrant() {
    const projectId = this.attr(`project-id`);
    const sharableEntityType = this.attr(`type`);
    const entityId = this.attr(`id`);
    const canEdit = this.state.currentGrants.data.projectShares[0].canEdit;

    this.update(
      produce((draft: SharingModalState) => {
        const shares = draft.currentGrants.data;
        shares.projectShares = [];
      }),
    );

    try {
      await sharedEntitiesClient.removeGrants({projectId, entityId, sharableEntityType}, {projectShares: [projectId]});

      this.track(track.removeProjectGrant(canEdit));
    } catch (err) {
      reporter.error(err, `Failed to remove sharing grant`);
      this.getCurrentShareGrants();
    }
  }

  async updateProjectShareGrant(canEdit: boolean) {
    const projectId = this.attr(`project-id`);
    const sharableEntityType = this.attr(`type`);
    const entityId = this.attr(`id`);

    this.update(
      produce((draft: SharingModalState) => {
        draft.currentGrants.data.projectShares[0].canEdit = canEdit;
      }),
    );

    try {
      await sharedEntitiesClient.updateGrants(
        {projectId, entityId, sharableEntityType},
        {projectShares: this.state.currentGrants.data.projectShares},
      );
      this.track(track.updateProjectGrant(canEdit));
    } catch (err) {
      reporter.error(err, `Failed to update sharing grant`);
      this.getCurrentShareGrants();
    }
  }

  async updateProjectShareGrantNew(shareLevel: ProjectShareLevel) {
    const projectId = this.attr(`project-id`);
    const sharableEntityType = this.attr(`type`);
    const entityId = this.attr(`id`);

    const existingShare = this.state.currentGrants.data.projectShares[0];

    this.update(
      produce((draft: SharingModalState) => {
        if (shareLevel === ProjectShareLevel.Off) {
          draft.currentGrants.data.projectShares = [];
        } else if (existingShare) {
          draft.currentGrants.data.projectShares[0].canEdit = shareLevel === ProjectShareLevel.Edit;
        } else {
          draft.currentGrants.data.projectShares = [{id: projectId, canEdit: shareLevel === ProjectShareLevel.Edit}];
        }
      }),
    );

    if (shareLevel === ProjectShareLevel.Off) {
      try {
        await sharedEntitiesClient.removeGrants(
          {projectId, entityId, sharableEntityType},
          {projectShares: [projectId]},
        );
        this.track(track.removeProjectGrant(existingShare.canEdit));
      } catch (err) {
        reporter.error(err, `Failed to remove project sharing grant`);
        this.getCurrentShareGrants();
      }
    } else if (existingShare) {
      try {
        await sharedEntitiesClient.updateGrants(
          {projectId, entityId, sharableEntityType},
          {projectShares: this.state.currentGrants.data.projectShares},
        );
        this.track(track.updateProjectGrant(shareLevel === ProjectShareLevel.Edit));
      } catch (err) {
        reporter.error(err, `Failed to update project sharing grant`);
        this.getCurrentShareGrants();
      }
    } else {
      try {
        await sharedEntitiesClient.updateGrants(
          {projectId, entityId, sharableEntityType},
          {projectShares: this.state.currentGrants.data.projectShares},
        );
        this.track(track.newProjectGrant());
      } catch (err) {
        reporter.error(err, `Failed to add project sharing grants`);
        this.getCurrentShareGrants();
      }
    }
  }

  async getPublicDashboardLinks(): Promise<void> {
    const entityId = this.attr(`id`);
    if (!this.helpers.publicDashboard.showPublicDashboardSwitch()) {
      this.update(
        produce((draft: SharingModalState) => {
          draft.publicDashboardLinks.loading = LoadingStatus.Success;
        }),
      );
      return;
    }
    this.update(
      produce((draft: SharingModalState) => {
        draft.publicDashboardLinks.loading = LoadingStatus.Pending;
      }),
    );
    try {
      const response = await publicDashboardLinksClient.getPublicDashboardLinksByDashboardId({
        dashboardId: entityId.toString(),
        projectId: this.attr(`project-id`).toString(),
      });
      const currentPublicDashboard = response.find(
        (publicDashboardLink) =>
          publicDashboardLink.dashboard_id === Number(entityId) &&
          publicDashboardLink.workspace_id === parseInt(getWorkspaceId()) &&
          publicDashboardLink.deleted === null,
      );
      this.update(
        produce((draft: SharingModalState) => {
          draft.publicDashboardLinks.loading = LoadingStatus.Success;
          draft.publicDashboardLinks.data = response;
          draft.currentPublicDashboardLink = currentPublicDashboard ?? null;
        }),
      );
      this.helpers.publicDashboard.updatePublicDashboardEnabled();
    } catch (error) {
      reporter.error(error, `Failed to fetch public dashboard links for the workspace: ${getWorkspaceId().toString()}`);
      produce((draft: SharingModalState) => {
        draft.publicDashboardLinks.loading = LoadingStatus.Failed;
        draft.publicDashboardLinks.data = null;
      });
    }
  }

  async createPublicDashboardLink(workspaceId: string): Promise<void> {
    const entityId = this.attr(`id`);
    try {
      const response = await publicDashboardLinksClient.createPublicDashboardLink({
        dashboardId: entityId.toString(),
        workspaceId,
      });
      this.update(
        produce((draft: SharingModalState) => {
          draft.currentPublicDashboardLink = response;
          draft.publicDashboardLinks.data.push(response);
        }),
      );
      addToast({
        message: `Public Board enabled for this Data View`,
        type: ToastType.SUCCESS,
      });
      this.track(track.publicDashboardUpdated(true));
      this.helpers.publicDashboard.updatePublicDashboardEnabled();
      // CustomEvent to update PublicBadge in query-report-header
      this.dispatchEvent(
        new CustomEvent(`PublicDashboardUpdated`, {
          detail: {numLinks: this.state.publicDashboardLinks.data.length},
          bubbles: true,
        }),
      );
    } catch (error) {
      reporter.error(error, `Failed to create public board URL for the board: ${entityId}`);
      addToast({
        message: `Something went wrong`,
        type: ToastType.ERROR,
      });
    }
  }

  handleOverflowSelect(ev, {workspaceId}: {workspaceId: number}) {
    // When we open the sharing modal with open-password-row-item
    // we show Public Dashboard Details page with an affected password row open
    // If there are any subsequent user actions to open other password rows
    // we must clear this attribute
    this.removeAttribute(`open-password-row-item`);
    if (ev.detail.value === OverflowActions.Deactivate) {
      this.deletePublicDashboardLink(workspaceId.toString());
    } else if (ev.detail.value === OverflowActions.Edit) {
      const expandedPublicDashboardRows = new Set([workspaceId]);
      this.update({expandedPublicDashboardRows});
    }
  }

  async deletePublicDashboardLink(workspaceId: string): Promise<void> {
    const entityId = this.attr(`id`);
    try {
      await publicDashboardLinksClient.deletePublicDashboardLink({
        dashboardId: entityId.toString(),
        workspaceId,
      });

      this.update(
        produce((draft: SharingModalState) => {
          const idx = draft.publicDashboardLinks.data.findIndex((p) => p.workspace_id === parseInt(workspaceId, 10));
          draft.publicDashboardLinks.data = removeByIndex(draft.publicDashboardLinks.data, idx);
          draft.currentPublicDashboardLink = null;
        }),
      );
      this.track(track.publicDashboardUpdated(false));
      this.helpers.publicDashboard.updatePublicDashboardEnabled();

      // CustomEvent to update PublicBadge in query-report-header
      this.dispatchEvent(
        new CustomEvent(`PublicDashboardUpdated`, {
          detail: {numLinks: this.state.publicDashboardLinks.data.length},
          bubbles: true,
        }),
      );
      addToast({
        message: `Public URL deactivated.`,
        type: ToastType.SUCCESS,
        undoable: true,
        undoCallback: () => this.createPublicDashboardLink(workspaceId),
      });
    } catch (error) {
      console.error(error);
      reporter.error(error, `Failed to delete public dashboard link for the dashboard: ${entityId}`);
      addToast({
        message: `Something went wrong`,
        type: ToastType.ERROR,
      });
    }
  }

  async getAvailableMembers(): Promise<void> {
    const projectId = this.attr(`project-id`);

    // Free plan requires availableMembers in disabled mode to figure out owner.
    if (this.attr(`disabled`) && this.helpers.isSharingEnabledForProject()) {
      this.update(
        produce((draft: SharingModalState) => {
          draft.availableMembers.loading = LoadingStatus.Success;
          draft.availableMembers.data = [];
        }),
      );
      return;
    }
    this.update(
      produce((draft: SharingModalState) => {
        draft.availableMembers.loading = LoadingStatus.Pending;
      }),
    );

    try {
      const response = await apiClient.get<ProjectMemberResponse>(`/api/app/projects/${projectId}/granted-users`);
      this.update(
        produce((draft: SharingModalState) => {
          draft.availableMembers.loading = LoadingStatus.Success;
          const members = uniqueBy<ProjectMember>(
            response.members.map(
              (member): ProjectMember => ({
                id: member.id,
                name: member.name,
                email: member.email,
                role: member.role,
                roleOrder: member.role_order,
                targetType: member.target_type,
              }),
            ),
            `id`,
          );
          draft.availableMembers.data = members;
          this.fromSessionData()
            .filter((item) => isProjectMember(item))
            .forEach((member) => draft.pendingGrants.add(members.find((m) => m.id === member.id)));
        }),
      );
    } catch (err) {
      if (err.status === 403) {
        this.update(
          produce((draft: SharingModalState) => {
            draft.availableMembers.loading = LoadingStatus.Success;
            draft.availableMembers.data = [];
          }),
        );

        return;
      }

      reporter.error(err, `Failed to fetch existing project members`);
      this.update(
        produce((draft: SharingModalState) => {
          draft.availableMembers.loading = LoadingStatus.Failed;
          draft.availableMembers.error = err;
        }),
      );
    }
  }

  async getAvailableTeams(): Promise<void> {
    const projectId = this.attr(`project-id`);
    if (this.attr(`disabled`)) {
      this.update(
        produce((draft: SharingModalState) => {
          draft.availableTeams.loading = LoadingStatus.Success;
          draft.availableTeams.data = [];
        }),
      );
      return;
    }

    this.update(
      produce((draft: SharingModalState) => {
        draft.availableTeams.loading = LoadingStatus.Pending;
      }),
    );

    try {
      const response: any = await apiClient.get(`/api/app/projects/${projectId}/granted-teams`);
      this.update(
        produce((draft: SharingModalState) => {
          draft.availableTeams.loading = LoadingStatus.Success;
          draft.availableTeams.data = response.teams as TeamObj[];
          this.fromSessionData()
            .filter((item) => !isProjectMember(item))
            .forEach((team) => draft.pendingGrants.add(response.teams.find((t) => t.id === team.id)));
        }),
      );
    } catch (err) {
      reporter.error(err, `Failed to fetch teams for the project`);
      this.update(
        produce((draft: SharingModalState) => {
          draft.availableTeams.loading = LoadingStatus.Failed;
          draft.availableTeams.error = err;
        }),
      );
    }
  }

  async getCurrentShareGrants(takeSnapshot = false) {
    const projectId = this.attr(`project-id`);
    const sharableEntityType = this.attr(`type`);
    const entityId = this.attr(`id`);

    // if no entityId present. Do not get currentShareGrants
    if (!entityId) return;
    this.update(
      produce((draft: SharingModalState) => {
        draft.currentGrants.loading = LoadingStatus.Pending;
      }),
    );

    try {
      const response = await sharedEntitiesClient.getGrants({projectId, entityId, sharableEntityType});
      this.update(
        produce((draft: SharingModalState) => {
          draft.currentGrants.loading = LoadingStatus.Success;
          draft.currentGrants.data = response;
          if (takeSnapshot) {
            draft.currentGrantsSnapshot.data = {...response};
          }
        }),
      );
    } catch (err) {
      reporter.error(`[Share Modal] Failed to fetch existing share grants`, err.message);
      this.update(
        produce((draft: SharingModalState) => {
          draft.currentGrants.loading = LoadingStatus.Failed;
          draft.currentGrants.error = err;
        }),
      );
    }
    if (this.state.currentGrants.loading === LoadingStatus.Success) {
      // getShortUrl depends on currentGrant data in paid projects
      await this.getShortUrl();
    }
  }

  clearSessionData() {
    window.sessionStorage.removeItem(`${this.attr(`project-id`)}:${this.attr(`type`)}:${this.attr(`id`)}`);
  }

  updateSessionData() {
    window.sessionStorage.setItem(
      `${this.attr(`project-id`)}:${this.attr(`type`)}:${this.attr(`id`)}`,
      JSON.stringify(Array.from(this.state.pendingGrants)),
    );
  }

  fromSessionData() {
    const values = window.sessionStorage.getItem(`${this.attr(`project-id`)}:${this.attr(`type`)}:${this.attr(`id`)}`);
    if (values) {
      return JSON.parse(values);
    }
    return [];
  }

  track({eventName, props}: SharingTrackArgs) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix,
      props: {
        ...commonSharingTrackProps(),
        ...props,
      },
    });
  }

  connectedCallback() {
    super.connectedCallback();
    currentProjectStore.subscribe(this.currentProjectStoreCallback).fetch();

    /** Modal is opened from a Share button(old) or Manage Sharing (new dropdown version)*/
    if (this.attr(`open`)) {
      this.track(track.opened(this.attr(`type`), this.attr(`id`)));
    }

    /** In Free plan, we always fetch short URL when modal opens given free plan can't fetch current grants*/
    if (this.helpers.shouldShowUpsellBanner()) {
      this.getShortUrl(true);
    }

    /** When a caller wants to open the sharing modal with a certain view, we respect that view*/
    if (this.attr(`open-as`)) {
      this.update({modalViewType: this.attr(`open-as`)});
    }

    const currentProject = this.getContext(`currentProject`).get()?.value;
    this.update({upsellInfo: getUpsellInfo(currentProject, ModalTrigger.ADVANCED_SHARING, this.getContext(`i18n`))});
  }

  disconnectedCallback() {
    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    super.disconnectedCallback();
  }

  attributeChangedCallback(attr: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(attr, oldValue, newValue);

    /** Modal is opened from Save and Share */
    if (this.initialized && attr === `open`) {
      if (this.attr(`open`)) {
        this.track(track.opened(this.attr(`type`), this.attr(`id`)));
      } else {
        const shareModalType = this.helpers.isSharingEnabledForProject()
          ? ShareModalType.Regular
          : ShareModalType.Upsell;
        this.track(track.closed(shareModalType));
      }
    }
  }
}

export const SharingModalEl = defineAndMakeJsxFactory(`mp-sharing-modal`, SharingModal);
export const SharingModalReact = panel2react(`mp-sharing-modal`, SharingModal);
